import { Box, Link, Stack } from "@mui/material";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import {
  BottomSheet,
  BottomSheetIllustrationContent,
  Illustration,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { TextButton } from "@src/appV2/redesign/system/ui-components";
import { useHistory } from "react-router-dom";

import { type PlacementDetail } from "../api/useGetPlacement";
import { getPlacementChatPath } from "../paths";
import { getVerifiedType, PLACEMENT_VERIFIED_TYPES } from "../utils/verified-placement";
import { BrowseMoreJobsButton } from "./BrowseMoreJobsButton";
import { BrowseMyJobsButton } from "./BrowseMyJobs";

interface PlacementApplicationSuccessBottomSheetProps {
  modalState: UseModalState;
  placementDetails: PlacementDetail;
}

export function PlacementApplicationSuccessBottomSheet(
  props: PlacementApplicationSuccessBottomSheetProps
) {
  const { modalState, placementDetails } = props;
  const history = useHistory();

  const verifiedType = getVerifiedType(placementDetails);

  function getApplicationSuccessTitleLine1() {
    if (verifiedType === PLACEMENT_VERIFIED_TYPES.REQUEST_INTERVIEW) {
      return "Interview request";
    }

    return "Application";
  }

  function getApplicationSuccessTitleLine2() {
    if (verifiedType === PLACEMENT_VERIFIED_TYPES.REQUEST_INTERVIEW) {
      return "submitted!";
    }

    return "sent!";
  }

  const handleClose = () => {
    modalState.closeModal();
  };

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={handleClose} />}
      footer={
        <DialogFooter orientation="vertical">
          <BrowseMoreJobsButton />
          <BrowseMyJobsButton
            applicationMethod={placementDetails.applicationMethod}
            buttonText="View my application"
            logMetadata={{
              workplaceId: placementDetails.workplace.id,
              placementId: placementDetails.id,
              buttonSource: "placement_details_page",
            }}
          />
        </DialogFooter>
      }
    >
      <Box sx={{ px: 5, py: 6 }}>
        <Box sx={{ py: 6, px: 4 }}>
          {placementDetails.status === "open" && (
            <Stack spacing={8} alignItems="center" justifyContent="center">
              <Illustration type="request-sent" />
              <Stack spacing={2}>
                <Text variant="h3" textAlign="center" sx={{ fontSize: "2.5rem" }}>
                  {getApplicationSuccessTitleLine1()}
                </Text>
                {verifiedType !== PLACEMENT_VERIFIED_TYPES.APPLY_ON_WEB && (
                  <Text variant="h3" textAlign="center" sx={{ fontSize: "2.5rem" }}>
                    {getApplicationSuccessTitleLine2()}
                  </Text>
                )}

                {verifiedType === PLACEMENT_VERIFIED_TYPES.REQUEST_INTERVIEW && (
                  <Text variant="body2" textAlign="center">
                    {placementDetails.workplace.name} will get back to you in 3 business days,
                    through your Clipboard Chat. If you don&apos;t hear back, you can reach out to
                    them for an update{" "}
                    <TextButton
                      LinkComponent={Link}
                      onClick={() => {
                        history.push(
                          getPlacementChatPath(
                            placementDetails.id,
                            placementDetails.workplace.id,
                            "chat-with-placement-candidate-via-job-request"
                          )
                        );
                      }}
                    >
                      here
                    </TextButton>
                    .
                  </Text>
                )}
              </Stack>
            </Stack>
          )}
          {placementDetails.status === "waitlist" && (
            <BottomSheetIllustrationContent
              title="Thank you for joining the waitlist"
              illustrationType="clock"
              description="You were placed on a waitlist because this job is not currently open. As soon as it becomes open, you'll be invited for an interview."
            />
          )}
        </Box>
      </Box>
    </BottomSheet>
  );
}
